<template>
  <div :class="['production-summary', showStickyHeader ? 'production-summary-sticky-header' : '']">
    <Spinner v-if="loading" />
    <table width="100%" border="0" v-if="!loading">
      <thead>
        <tr>
          <th :width="titleColumnWidth"> </th>
          <th width="10%" class="text-center production-cell production-stage-avatar-cell">
            <Tooltip label="Order Started">
              <i class="fas fa-play-circle"></i>
            </Tooltip>
          </th>
          <th width="10%" class="text-center production-cell production-stage-avatar-cell" v-for="stage in productionStagesUsed" :key="stage.id">
            <Tooltip :label="displayNameForStage(stage)">
              <div class="production-stage-avatar" :style="stageAvatarStyles(stage)">
                {{stage.avatarName}}
                <div v-if="stage.isExternal" class="production-stage-external-badge"> </div>
              </div>
            </Tooltip>
          </th>
          <th width="10%" class="text-center production-cell production-stage-avatar-cell">
            <Tooltip :label="order.despatchTaskType.name">
              <i :class="['fas', getTaskTypeIcon(order.despatchTaskType)]"></i>
            </Tooltip>
          </th>
          <th width="10%" class="text-center production-cell production-stage-avatar-cell">
            <Tooltip label="Completed and Despatched">
              <i class="far fa-check-circle"></i>
            </Tooltip>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr class="production-line production-order-header border-bottom">
          <td> </td>
          <td class="text-center production-cell">
            <div class="production-tally">Order Started<br />{{order.dateOrderCreated | formatDateCustom('Do MMM h:mma')}}</div>
          </td>
          <td class="text-center production-cell" v-for="stage in productionStagesUsed" :key="stage.id">
          </td>
          <td class="text-center production-cell">
            
          </td>
          <td class="text-center production-cell">
            <div class="production-tally">Order Estimated Completion<br /> {{order.dateCurrentlyDue | formatDateCustom('Do MMM h:mma')}}</div>
          </td>
        </tr>

        <template v-for="(group, groupIndex) in orderItemGroupList">
          <tr v-if="groupIndex > 0" class="production-overview-spacer" :key="`group-${group.id}-title-spacer`">
            <td :colspan="productionStagesUsed.length + 4"> </td>
          </tr>
          <tr :key="`group-${group.id}-title`">
            <td>
              <h4>{{group.title}}</h4>
            </td>
          </tr>

          <template v-for="(item, itemIndex) in group.itemList">
            <!-- Order Item Row -->
            <tr :key="`order-item-${item.id}`" :class="{'production-line': true, 'production-line-cancelled': isOrderItemCancelled(item)}" v-if="isOrderItemVisible(item)">
              <td class="font-weight-bold" :style="getOrderItemPaddingStyle(item)">
                <template v-if="item.isProcessedItem || isOrderItemTemplate(item) || isOrderItemAssembly(item)">
                  <ExpandCollapseButton :expanded="isOrderItemExpanded(item)" @update="setOrderItemExpanded(item, $event)" class="mr-2" />
                  <a @click.prevent="toggleOrderItemDisplay(item)" :class="{'production-item-cancelled': item.isCancelled}">{{item.quantity}}x {{item.stock.name}} - {{item.glassHeight}}x{{item.glassWidth}}</a>
                </template>
                <template v-if="!item.isProcessedItem && !isOrderItemTemplate(item) && !isOrderItemAssembly(item)">
                  <ExpandCollapseButton :disabled="true" class="mr-2" />
                  <span :class="{'production-item-stock': true, 'production-item-cancelled': item.isCancelled}">{{item.quantity}}x {{item.stock.name}} - {{item.glassHeight}}x{{item.glassWidth}}</span>
                </template>
                <div class="ml-4" v-if="orderItemRecutCount(item) > 0">
                  <LabelBadge labelClass="label-order-type-nonChargeableRework">{{orderItemRecutCount(item)}}x Recut</LabelBadge>
                </div>
              </td>

              <!-- Processed Items: Empty Stages -->
              <td colspan="2" width="10%" class="text-center production-cell" v-if="isOrderItemWaitingPanels(item)">
                Pending Production Item Creation
              </td>

              <template v-if="isOrderItemGeneral(item) || isOrderItemGlassStock(item) || item.panelList.length > 0">
                <td width="10%" class="text-center production-cell" v-for="(segment, segmentIndex) in item.segments" :key="segmentIndex">
                  <div :class="{
                    'production-item': segmentIndex > 0,
                    'production-item-skip': !segment.hasDot,
                    'production-item-progress': segment.prevActive && !segment.prevCompleted,
                    'production-item-complete': segment.prevCompleted,
                    'production-item-partial-complete': segment.prevCompleted && segment.isTransferRequired && !segment.transferCompleted }">

                    <div v-if="segment.hasDot && segment.isTransferRequired"
                      :class="{
                        'production-transfer': true, 
                        'production-transfer-waiting': segment.waiting,
                        'production-transfer-progress': segment.transferActive && !segment.transferCompleted,
                        'production-transfer-complete': segment.transferCompleted
                      }" @click.prevent="displayItemTransferPopover(item, null, segment, $event)">
                      <i class="fas fa-random"></i>
                    </div>
                    <div :class="{
                      'production-dot': segment.hasDot,
                      'production-dot-clickable': segment.hasDot,
                      'production-dot-waiting': segment.hasDot && segment.waiting,
                      'production-dot-progress': segment.hasDot && segment.active && !segment.completed,
                      'production-dot-complete': segment.hasDot && segment.completed
                      }" @click.prevent="displayOrderItemPopover(item, $event)"></div>
                    <div class="production-tally production-tally-offset" v-if="segment.totalCount > 0 && segment.completedCount > 0 && segment.completedCount < segment.totalCount">{{segment.completedCount}} / {{segment.totalCount}}</div>
                  </div>
                </td>
              </template>

            </tr>

            <!-- Panel -->
            <template v-if="isOrderItemExpanded(item) && isOrderItemVisible(item)">
              <template v-for="panel in item.panelList">
                <tr :key="`order-item-${item.id}-panel${panel.id}`" :class="['production-line', {'production-line-recut': isPanelRecutStopped(panel), 'production-line-cancelled': isPanelCancelled(panel)}]">
                  <td class="panel-title-cell" :style="getOrderItemPaddingStyle(item)">
                    <i :class="['fas', 'fa-sm', 'fa-caret-right', 'mr-2', 'text-muted', 'panel-title']"></i>
                    <a :class="{'production-panel-recut-stopped': isPanelRecutStopped(panel), 'production-panel-recut': panel.isRecut, 'production-panel-cancelled': isPanelCancelled(panel), 'production-panel-completed': isPanelCompleted(panel)}" @click.prevent="displayPanelPopover(item, panel, null, $event)">{{panel.name}} #{{(panel.panelIndex + 1)}} - {{panel.barcode}}</a>
                    <div class="ml-4" v-if="panel.isRecut || isPanelCancelled(panel) || isPanelRecutStopped(panel)">
                      <LabelBadge labelClass="label-production-status-recut" v-if="isPanelRecutStopped(panel)">Stopped: Recut</LabelBadge>
                      <LabelBadge labelClass="label-order-status-denied" v-if="isPanelCancelled(panel)">Stopped: Cancelled</LabelBadge>
                      <LabelBadge labelClass="label-order-type-nonChargeableRework" v-if="panel.isRecut">Recut</LabelBadge>
                    </div>
                  </td>

                  <td width="10%" class="text-center production-cell" v-for="(segment, segmentIndex) in panel.segments" :key="segmentIndex">
                    <div :class="{
                      'production-item': segmentIndex > 0,
                      'production-item-skip': !segment.hasDot,
                      'production-item-progress': segment.prevActive && !segment.prevCompleted,
                      'production-item-complete': segment.prevCompleted,
                      'production-item-partial-complete': segment.prevCompleted && segment.isTransferRequired && !segment.transferCompleted }">

                      <div v-if="segment.hasDot && segment.isTransferRequired"
                      :class="{
                        'production-transfer': true, 
                        'production-transfer-waiting': segment.waiting,
                        'production-transfer-progress': segment.transferActive && !segment.transferCompleted,
                        'production-transfer-complete': segment.transferCompleted
                      }" @click.prevent="displayItemTransferPopover(item, panel, segment, $event)">
                      <i class="fas fa-random"></i>
                    </div>

                      <div :class="{
                        'production-dot': segment.hasDot,
                        'production-dot-clickable': segment.hasDot,
                        'production-dot-waiting': segment.hasDot && segment.waiting,
                        'production-dot-progress': segment.hasDot && segment.active && !segment.completed,
                        'production-dot-complete': segment.hasDot && segment.completed
                        }" @click.prevent="displayPanelPopover(item, panel, segment, $event)"></div>
                    </div>
                  </td>
                </tr>
              </template>

              <!-- Spacers -->
              <tr class="production-line-spacer" :key="`order-item-${item.id}-spacer`" v-if="itemIndex < orderItemDetailList.length-1">
                <td> </td>
              </tr>
              <tr class="production-line-spacer" v-show="!compactMode && itemIndex < orderItemDetailList.length-1" :key="`order-item-${item.id}-spacer-extra`">
                <td> </td>
              </tr>

            </template>
          </template>
        </template>

        <tr class="production-overview-spacer">
          <td :colspan="productionStagesUsed.length + 4"> </td>
        </tr>
      </tbody>
    </table>
    <OrderProductionPanelPopover ref="panelPopover" :showButtons="showPopoverButtons" />
    <OrderProductionPanelStageTaskPopover ref="panelStageTaskPopover" />
    <OrderProductionPanelDespatchPopover ref="panelDespatchPopover" />
    <OrderProductionOrderItemPopover ref="orderItemPopover" />
    <OrderProductionTransferPopover ref="orderItemTransferPopover" />
  </div>
</template>

<style lang="scss" scoped>

</style>

<script>
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import Utils from '@/utils/utils'
import colourMixin from '@/mixins/colour'
import orderItemMixin from '@/modules/orders/mixins/orderItemMixin'
import taskDespatchMixin from '@/modules/tasks/mixins/taskDespatchMixin'

import OrderProductionPanelPopover from '@/modules/orders/components/orderProductionPanelPopover'
import OrderProductionPanelStageTaskPopover from '@/modules/orders/components/orderProductionPanelStageTaskPopover'
import OrderProductionPanelDespatchPopover from '@/modules/orders/components/orderProductionPanelDespatchPopover'
import OrderProductionOrderItemPopover from '@/modules/orders/components/orderProductionOrderItemPopover'
import OrderProductionTransferPopover from '@/modules/orders/components/orderProductionTransferPopover'

import { InventoryStockTypes, ProductionPanelStatusTypes, ProductionPanelStageTaskStatusTypes, ProductionTransferStatusTypes, TaskStatusTypes, TaskItemTypes, TaskItemStatusTypes, ProductionPanelTypeLookup, ProductionPanelTypes } from '@/store/types'

export default {
  name: 'OrderProductionPreview',
  mixins: [colourMixin, orderItemMixin, taskDespatchMixin],
  components: {
    OrderProductionPanelPopover,
    OrderProductionPanelStageTaskPopover,
    OrderProductionPanelDespatchPopover,
    OrderProductionOrderItemPopover,
    OrderProductionTransferPopover
  },
  props: {
    order: Object,
    compactMode: {
      type: Boolean,
      default: true
    },
    showPopoverButtons: {
      type: Boolean,
      default: true
    },
    showStickyHeader: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: true,
      productionPanels: [],
      productionStagesUsed: [],
      taskList: [],
      orderItemSettings: {},

      orderItemDetailList: [],
      orderItemGroupList: []
    }
  },
  watch: {
    'order.id': function(newVal, oldVal) {
      // Change subscription
      this.unsubscribeEndpoint(`/orders/${oldVal}/production`)
      this.unsubscribeEndpoint(`/orders/${oldVal}`)

      this.subscribeEndpoint(`/orders/${this.order.id}/production`, () => {
        this.requestPanelData()
      })
      this.subscribeEndpoint(`/orders/${this.order.id}`, () => {
        this.requestPanelData()
      })

      this.requestPanelData()
    }
  },
  created() {
    this.ProductionPanelTypeLookup = ProductionPanelTypeLookup
  },
  mounted() {
    this.requestPanelData()

    this.subscribeEndpoint(`/orders/${this.order.id}/production`, () => {
      this.requestPanelData()
    })
    this.subscribeEndpoint(`/orders/${this.order.id}`, () => {
      this.requestPanelData()
    })
    this.subscribeEndpoint(`/orders/production`, (data) => {
      // Check if this order is in the list
      if(_.isArray(data.orderIdList)) {
        if(data.orderIdList.indexOf(this.order.id) != -1)
          this.requestPanelData()
      }
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint(`/orders/${this.order.id}/production`)
    this.unsubscribeEndpoint(`/orders/${this.order.id}`)
    this.unsubscribeEndpoint(`/orders/production`)
  },
  computed: {
    titleColumnWidth() {
      // Stage/Despatch columns are fixed width. We adjust the title column to take up the remaining space
      var stageColumnWidth = 10
      var startedColumnWidth = stageColumnWidth
      var despatchColumnWidth = stageColumnWidth
      var completedColumnWidth = stageColumnWidth
      let stageCount = this.productionStagesUsed.length

      return (100 - (stageCount * stageColumnWidth + despatchColumnWidth + startedColumnWidth + completedColumnWidth)) + '%'
    }
  },
  methods: {
    requestPanelData() {
      this.loadingError = ''

      var requestList = [
        this.$apiService.ordersService.getOrderProductionPanelList(this.order.id)
      ]
      if(this.userHasReadPermission(this.PermissionTypes.TaskPermissionTypes.task))
        requestList.push(this.$apiService.tasksService.getTaskList({ filter: { orderId: this.order.id } }))
      
      Promise.all(requestList)
      .then(results => {
        this.productionPanels = results[0].data
        this.taskList = results.length > 1 ? results[1].data : []

        // Calculate the production stages used
        this.productionStagesUsed = []
        this.productionPanels.forEach(panel => {

          panel.productionPanelStageTasks.sort((task1, task2) => task1.taskOrder - task2.taskOrder)

          panel.productionPanelStageTasks.forEach(panelTask => {
            if(panelTask.productionStage) {
              var hasStage = this.productionStagesUsed.find(stage => stage.id == panelTask.productionStage.id)
              if(!hasStage) {
                var stage = _.clone(panelTask.productionStage)

                // Calculate some fields for the stage
                stage.avatarName = ''
                if(!stage.logoImageURL)
                  stage.avatarName = Utils.calculateAvatarInitials(stage.name)

                this.productionStagesUsed.push(stage)
              }
            }
          })
        })

        this.productionStagesUsed.sort((stage1, stage2) => stage1.sortOrder - stage2.sortOrder)

        // Pre-calculate the details
        this.calculatePreviewDetails()
      })
      .catch(err => {
        console.error(err)
        this.loadingError = 'Unable to load order production status'
      })
      .finally(() => {
        this.loading = false
      })
    },
    calculatePreviewDetails() {
      if(!this.order.orderItems)
        return

      var orderItemDetailList = _.cloneDeep(this.order.orderItems.filter(item => {
        if(item.stockType == InventoryStockTypes.glass) {      
          // Always show root glass items
          if(item.parentOrderItemId == null)
            return true

          // If they are part of a group (eg, glass unit) then only show them if there are panels linked to this glass item
          let linkedPanels = this.productionPanels.filter(panel => panel.orderItemId == item.id)
          if(linkedPanels.length > 0)
            return true
        }

        // Always show template items
        if(item.stockType == InventoryStockTypes.template)
          return true

        // Always show assembly items
        if(item.stockType == InventoryStockTypes.assembly)
          return true
        
        return false
      }))

      // Calculate panels per order item & all the stats
      orderItemDetailList.forEach(orderItem => {
        orderItem.panelList = []
        orderItem.stages = []
        orderItem.segments = []

        let orderItemDespatchTaskItems = this.findActiveTaskItemsForOrderItem(orderItem)
        let orderItemTransferTaskItems = this.findActiveTransferTaskItemsForOrderItem(orderItem)

        orderItem.panelList = _.cloneDeep(this.productionPanels.filter(panel => panel.orderItemId == orderItem.id))
        orderItem.validPanelList = orderItem.panelList.filter(panel => {
          return panel.status != ProductionPanelStatusTypes.recut && panel.status != ProductionPanelStatusTypes.cancelled
        })

        orderItem.panelList.forEach(panel => {

          panel.name = 'Panel'
          if(panel.type == ProductionPanelTypes.assembly)
            panel.name = 'Assembly Item'

          panel.segments = [{
            id: 'start',
            name: 'Start',
            isStart: true,
            hasDot: true,
            waiting: false,
            active: false,
            completed: true,
            cancelled: orderItem.isCancelled
          }]
          this.productionStagesUsed.map(stage => {
            let panelTaskIndex = panel.productionPanelStageTasks.findIndex(panelTask => panelTask.productionStage.id == stage.id)
            let panelStageInfo = this.createSegment({ id: stage.id, name: stage.name, isStage: true, cancelled: orderItem.isCancelled })
            let panelHasTransferTask = orderItemTransferTaskItems.find(taskItem => taskItem.taskItemLinkedItems.some(linkedItem => linkedItem.productionPanelId == panel.id && linkedItem.productionStageId == stage.id))

            if(panelTaskIndex != -1) {
              var panelTask = panel.productionPanelStageTasks[panelTaskIndex]

              panelStageInfo.hasDot = true
              panelStageInfo.active = panelTask.status == ProductionPanelStageTaskStatusTypes.production
              panelStageInfo.completed = panelTask.status == ProductionPanelStageTaskStatusTypes.completed
              panelStageInfo.isTransferRequired = panelTask.isTransferRequired
              panelStageInfo.transferStatus = panelTask.transferStatus
              panelStageInfo.accountLocationId = panelTask.accountLocationId
              panelStageInfo.stageTask = panelTask

              if(panelStageInfo.isTransferRequired) {
                panelStageInfo.transferActive = panelTask.transferStatus == ProductionTransferStatusTypes.inProgress || panelHasTransferTask
                panelStageInfo.transferCompleted = panelTask.transferStatus == ProductionTransferStatusTypes.completed

                // If this stage is completed, just flag transfer as completed
                if(panelStageInfo.completed) {
                  panelStageInfo.transferActive = false
                  panelStageInfo.transferCompleted = true
                }
              }

              if(panelTaskIndex > 0) {
                let prevStageTask = panel.productionPanelStageTasks[panelTaskIndex-1]
                if(prevStageTask.status == ProductionPanelStageTaskStatusTypes.completed && panelTask.status == ProductionPanelStageTaskStatusTypes.pending) {
                  panelStageInfo.waiting = true
                }

                panelStageInfo.prevStageTask = prevStageTask
              } else if(panelTaskIndex == 0) {
                // First task is always waiting
                panelStageInfo.waiting = true
              }

              if(panelTaskIndex < panel.productionPanelStageTasks.length-1) {
                panelStageInfo.nextStageTask = panel.productionPanelStageTasks[panelTaskIndex+1]
              }
            }

            panel.segments.push(panelStageInfo)
          })

          let panelHasDespatchTransferTask = orderItemTransferTaskItems.some(taskItem => taskItem.taskItemLinkedItems.some(linkedItem => linkedItem.productionPanelId == panel.id && linkedItem.productionStageId == null))
          let orderItemHasPickupTask = orderItemTransferTaskItems.some(taskItem => taskItem.type == TaskItemTypes.transferPickup)

          // If panel is at same location as order & currently in despatch/completed, then the despatch has been completed
          let isDespatchTransferCompleted = false
          if(panel.hasDespatchTransfers && !panel.isPendingTransfer && panel.status == ProductionPanelStatusTypes.despatch)
            isDespatchTransferCompleted = true
          if(panel.status == ProductionPanelStatusTypes.completed)
            isDespatchTransferCompleted = true


          // Despatch & Completion only exist for panels that are not internal items
          if(!panel.isInternalItem) {
            panel.segments.push(this.createSegment({
              id: 'despatch',
              name: 'Despatch',
              isDespatch: true,
              hasDot: true,
              waiting: panel.status == ProductionPanelStatusTypes.despatch,
              active: orderItemDespatchTaskItems.length > 0,
              completed: panel.status == ProductionPanelStatusTypes.completed,
              cancelled: orderItem.isCancelled,
              isTransferRequired: panel.hasDespatchTransfers,
              transferActive: panel.hasDespatchTransfers && !isDespatchTransferCompleted && (panelHasDespatchTransferTask || orderItemHasPickupTask),
              transferCompleted: isDespatchTransferCompleted,
              accountLocationId: this.order.accountLocationId,
              prevStageTask: panel.productionPanelStageTasks[panel.productionPanelStageTasks.length - 1]
            }))
            panel.segments.push(this.createSegment({
              id: 'completed',
              name: 'Completed',
              isCompleted: true,
              hasDot: true,
              completed: panel.status == ProductionPanelStatusTypes.completed,
              cancelled: orderItem.isCancelled
            }))
          }

          // Calculating skipping segments
          this.finishSegmentList(panel.segments)
        })

        // Build order item segment summary based on panels
        if(orderItem.panelList.length > 0 && !this.isOrderItemAssembly(orderItem))
          orderItem.segments = this.buildOrderItemSegmentList(orderItem, orderItem.panelList, orderItemDespatchTaskItems)

        // For assemblies, build the segments from ALL child panels
        if(this.isOrderItemAssembly(orderItem)) {
          let fullPanelList = this.buildOrderItemFullPanelList(orderItem)

          orderItem.segments = this.buildOrderItemSegmentList(orderItem, fullPanelList, orderItemDespatchTaskItems)
        }

        // Build segments for non-processed items
        if((orderItem.stockType == InventoryStockTypes.glass && !orderItem.isProcessedItem) || orderItem.stockType == InventoryStockTypes.general) {
          orderItem.segments = [{
            id: 'start',
            name: 'Start',
            isStart: true,
            hasDot: true,
            waiting: false,
            active: false,
            completed: true,
            cancelled: orderItem.isCancelled
          }]
          this.productionStagesUsed.map(stage => {
            var orderItemStageInfo = this.createSegment({ id: stage.id, isStage: true, cancelled: orderItem.isCancelled })

            orderItem.segments.push(orderItemStageInfo)
          })
          orderItem.segments.push(this.createSegment({
            id: 'despatch',
            name: 'Despatch',
            isDespatch: true,
            hasDot: true,
            waiting: orderItem.despatchedQuantity < orderItem.quantity,
            active: orderItemDespatchTaskItems.length > 0,
            completed: orderItem.despatchedQuantity >= orderItem.quantity,
            cancelled: orderItem.isCancelled
          }))
          orderItem.segments.push(this.createSegment({
            id: 'completed',
            name: 'Completed',
            isCompleted: true,
            hasDot: true,
            completed: orderItem.despatchedQuantity >= orderItem.quantity,
            cancelled: orderItem.isCancelled
          }))
        }

        this.finishSegmentList(orderItem.segments)        
      })

      this.orderItemDetailList = _.cloneDeep(orderItemDetailList)

      // Build group list
      this.orderItemGroupList = []
      this.buildOrderItemGroup('Processed Glass', { stockType: InventoryStockTypes.glass, isProcessedItem: true })
      this.buildOrderItemGroup('Assemblies', { stockType: InventoryStockTypes.assembly })
      this.buildOrderItemGroup('Glass Stock', { stockType: InventoryStockTypes.glass, isProcessedItem: false })
      this.buildOrderItemGroup('Templates', { stockType: InventoryStockTypes.template })
    },
    buildOrderItemSegmentList(orderItem, panelList, orderItemDespatchTaskItems) {
      var segmentList = []

      segmentList.push({
        id: 'start',
        name: 'Start',
        isStart: true,
        hasDot: true,
        waiting: false,
        active: false,
        completed: true,
        cancelled: orderItem.isCancelled
      })

      var validPanelList = panelList.filter(panel => {
        if(panel.status == ProductionPanelStatusTypes.recut || panel.status == ProductionPanelStatusTypes.cancelled)
          return false
        return true
      })

      // Stages
      this.productionStagesUsed.map(stage => {
        let stagePanels = panelList.filter(panel => {
          if(panel.status == ProductionPanelStatusTypes.recut || panel.status == ProductionPanelStatusTypes.cancelled)
            return false
          
          return panel.productionPanelStageTasks.some(panelTask => panelTask.productionStage.id == stage.id)
        })

        let orderItemStageSegment = this.createSegment({
          id: stage.id,
          name: stage.name,
          isStage: true,
          isOrderItem: true,
          completedCount: 0,
          cancelled: orderItem.isCancelled
        })

        if(stagePanels.length > 0) {
          orderItemStageSegment.hasDot = true         
          
          orderItemStageSegment.totalCount = stagePanels.length
          var activeCount = 0
          var waitingCount = 0

          stagePanels.forEach(panel => {
            let panelTaskIndex = panel.productionPanelStageTasks.findIndex(panelTask => panelTask.productionStage.id == stage.id)
            if(panelTaskIndex != -1) {
              let panelTask = panel.productionPanelStageTasks[panelTaskIndex]

              if(panelTask.status == ProductionPanelStageTaskStatusTypes.completed)
                orderItemStageSegment.completedCount++
              if(panelTask.status == ProductionPanelStageTaskStatusTypes.production)
                activeCount++
              if(panelTaskIndex > 0) {
                let prevStageTask = panel.productionPanelStageTasks[panelTaskIndex-1]
                if(prevStageTask.status == ProductionPanelStageTaskStatusTypes.completed && panelTask.status == ProductionPanelStageTaskStatusTypes.pending)
                  waitingCount++
              } else if(panelTaskIndex == 0) {
                waitingCount++
              }
            }
          })

          orderItemStageSegment.completed = orderItemStageSegment.completedCount == orderItemStageSegment.totalCount && orderItemStageSegment.completedCount > 0
          orderItemStageSegment.active = (activeCount + orderItemStageSegment.completedCount) > 0 && !orderItemStageSegment.completed
          orderItemStageSegment.waiting = waitingCount > 0 && !orderItemStageSegment.active && !orderItemStageSegment.completed
        }
        
        segmentList.push(orderItemStageSegment)
      })

      // Despatch & Completion only exist for panels that are not internal items
      var hasExternalItems = validPanelList.some(panel => !panel.isInternalItem)
      var panelDespatchCount = validPanelList.filter(panel => panel.status == ProductionPanelStatusTypes.despatch).length

      if(hasExternalItems) {
        segmentList.push(this.createSegment({
          id: 'despatch',
          name: 'Despatch',
          isDespatch: true,
          hasDot: true,
          waiting: panelDespatchCount > 0 && orderItem.despatchedQuantity < orderItem.quantity,
          active: orderItemDespatchTaskItems.length > 0,
          completed: orderItem.despatchedQuantity >= orderItem.quantity,
          cancelled: orderItem.isCancelled,
          isTransferRequired: false,
          transferActive: false,
          transferCompleted: false,
          accountLocationId: this.order.accountLocationId,
          prevStageTask: null
        }))
        segmentList.push(this.createSegment({
          id: 'completed',
          name: 'Completed',
          isCompleted: true,
          hasDot: true,
          completed: orderItem.despatchedQuantity >= orderItem.quantity,
          cancelled: orderItem.isCancelled
        }))
      }

      // Calculating skipping segments
      this.finishSegmentList(segmentList)

      return segmentList
    },
    buildOrderItemGroup(title, filterObj) {
     
      // Build tree for all root items
      var outputList = this.buildItemList(filterObj, null, [])
      if(outputList.length == 0)
        return

      // Setup group
      this.orderItemGroupList.push({
        id: uuidv4(),
        title: title,
        itemList: outputList
      })
    },
    buildItemList(filterObj, parentOrderItemId = null, outputList = [], level = 0) {
      var orderItemList = this.orderItemDetailList
      
      if(filterObj) {
        let filterKeys = Object.keys(filterObj)
        orderItemList = orderItemList.filter(orderItem => {
          return filterKeys.every(filterKey => orderItem[filterKey] == filterObj[filterKey])
        })
      }

      orderItemList.forEach(orderItem => {
        if(orderItem.parentOrderItemId == parentOrderItemId) {
          
          orderItem.level = level
          outputList.push(orderItem)

          // Children order items
          this.buildItemList(null, orderItem.id, outputList, level+1)
        }
      })

      return outputList
    },
    buildOrderItemPanelList(orderItem) {
      // Only directly linked panels to this order item
      var panelList = this.productionPanels.filter(panel => {
        return panel.orderItemId == orderItem.id
      })
      return panelList
    },
    buildOrderItemFullPanelList(orderItem, outputList = []) {
      var panelList = this.productionPanels.filter(panel => {
        return panel.orderItemId == orderItem.id
      })
      outputList = outputList.concat(panelList)

      let childOrderItems = this.order.orderItems.filter(childOrderItem => childOrderItem.parentOrderItemId == orderItem.id)
      childOrderItems.forEach(childOrderItem => {
        outputList = this.buildOrderItemFullPanelList(childOrderItem, outputList)
      })
      
      return outputList
    },

    isPanelRecutStopped(panel) {
      return panel.status == ProductionPanelStatusTypes.recut
    },
    isPanelCancelled(panel) {
      return panel.status == ProductionPanelStatusTypes.cancelled
    },
    isPanelCompleted(panel) {
      return panel.status == ProductionPanelStatusTypes.completed
    },
    stageAvatarStyles(stage) {
      var lightenAmount = 25      // Lighten the stage colour a bit to better suit the order page
      var bgColour = this.lightenColour(stage.highlightColour, lightenAmount)

      if(stage.logoImageURL)
        return { 'background-image': `url('${stage.logoImageURL}')`, 'background-size': 'contain' }

      return {
        'background-color': bgColour,
        'color': this.textColourForBackground(bgColour)
      }
    },
    displayNameForStage(stage) {
      return `${stage.name}${stage.isExternal ? ' - External' : ''}`.trim()
    },

    orderItemPanelList(orderItem) {
      return this.productionPanels.filter(panel => panel.orderItemId == orderItem.id)
    },
    findActiveTaskItemsForOrderItem(orderItem) {
      var taskItems = []
      this.taskList.filter(task => {

        if(task.taskItems) {
          taskItems = _.concat(taskItems, task.taskItems.filter(taskItem => {
            if(taskItem.status == TaskItemStatusTypes.completed || taskItem.status == TaskItemStatusTypes.cancelled)
              return false
            if(taskItem.type != TaskItemTypes.orderDespatch && taskItem.type != TaskItemTypes.general)
              return false

            if(taskItem.taskItemLinkedItems) {
              return taskItem.taskItemLinkedItems.find(linkItem => linkItem.orderItemId == orderItem.id) != null
            }
            return false
          }))
        }
      })

      return taskItems
    },
    findActiveTransferTaskItemsForOrderItem(orderItem) {
      var taskItems = []
      this.taskList.filter(task => {

        if(task.taskItems) {
          taskItems = _.concat(taskItems, task.taskItems.filter(taskItem => {
            if(taskItem.status == TaskItemStatusTypes.completed || taskItem.status == TaskItemStatusTypes.cancelled)
              return false
            if(taskItem.type != TaskItemTypes.transferInternal && taskItem.type != TaskItemTypes.transferExternal && taskItem.type != TaskItemTypes.transferPickup)
              return false

            if(taskItem.taskItemLinkedItems) {
              return taskItem.taskItemLinkedItems.some(linkItem => linkItem.orderItemId == orderItem.id)
            }
            return false
          }))
        }
      })

      return taskItems
    },

    displayPanelPopover(orderItem, panel, segment, evt) {
      if(segment && segment.isDespatch)
        this.$refs.panelDespatchPopover.displayFromElement(evt.target, panel, this.order, this.taskList)
      else if(segment && segment.isStage)
        this.$refs.panelStageTaskPopover.displayFromElement(evt.target, panel, segment)
      else
        this.$refs.panelPopover.displayFromElement(evt.target, orderItem, panel)
    },
    displayOrderItemPopover(orderItem, evt) {
      this.$refs.orderItemPopover.displayFromElement(evt.target, this.order, orderItem, this.taskList)
    },
    displayItemTransferPopover(orderItem, panel, segment, evt) {

      this.$refs.orderItemTransferPopover.displayFromElement(evt.target, this.order, orderItem, panel, this.taskList, segment, segment.prevStageTask, segment.stageTask, segment.nextStageTask)
    },

    isOrderItemExpanded(orderItem) {
      if(this.orderItemSettings[orderItem.id] === undefined) {
        // Default to expanded for non-compact mode
        this.$set(this.orderItemSettings, orderItem.id, !this.compactMode)
      }

      return !!this.orderItemSettings[orderItem.id]
    },
    isOrderItemVisible(orderItem) {
      if(orderItem.parentOrderItemId == null)
        return true

      return !!this.orderItemSettings[orderItem.parentOrderItemId]
    },
    setOrderItemExpanded(orderItem, expanded) {
      this.$set(this.orderItemSettings, orderItem.id, expanded)
    },
    getOrderItemPaddingStyle(orderItem) {
      var levelDistance = 20
      return {
        'padding-left': `${orderItem.level * levelDistance}px`
      }
    },
    toggleOrderItemDisplay(orderItem) {
      this.$set(this.orderItemSettings, orderItem.id, !(this.orderItemSettings[orderItem.id] || false))
    },
    isOrderItemGlass(orderItem) {
      return orderItem.stockType == InventoryStockTypes.glass
    },
    isOrderItemGeneral(orderItem) {
      return orderItem.stockType == InventoryStockTypes.general
    },
    isOrderItemGlassProcessed(orderItem) {
      return orderItem.stockType == InventoryStockTypes.glass && orderItem.isProcessedItem
    },
    isOrderItemGlassStock(orderItem) {
      return orderItem.stockType == InventoryStockTypes.glass && !orderItem.isProcessedItem
    },
    isOrderItemTemplate(orderItem) {
      return orderItem.stockType == InventoryStockTypes.template
    },
    isOrderItemAssembly(orderItem) {
      return orderItem.stockType == InventoryStockTypes.assembly
    },
    isOrderItemWaitingPanels(orderItem) {
      return (!orderItem.panelList || orderItem.panelList.length == 0) && (orderItem.isProcessedItem || this.isOrderItemTemplate(orderItem) || this.isOrderItemAssembly(orderItem))
    },
    isOrderItemCancelled(orderItem) {
      return orderItem.isCancelled
    },
    orderItemRecutCount(orderItem) {
      return orderItem.panelList.filter(panel => panel.isRecut).length
    },

    // Create a new segment object
    createSegment(values) {
      return _.merge({
        id: null,
        name: '',
        isOrderItem: false,
        isStage: false,
        hasDot: false,
        waiting: false,
        active: false,
        completed: false,
        prevCompleted: false,
        prevActive: false,
        isTransferRequired: false,
        totalCount: 0,
        completedCount: 0
      }, values)
    },

    // Finish setting up the segment list with skip calculations
    finishSegmentList(segments) {    
      segments.forEach((segment, segmentIndex) => {
        if(segmentIndex > 0) {
          var prevSegment = segments[segmentIndex-1]

          segment.prevCompleted = prevSegment.completed
          segment.prevActive = false
          if(!prevSegment.completed) {
            if(prevSegment.active && (segment.waiting || segment.active))
              segment.prevActive = true
            if(prevSegment.hasDot && prevSegment.waiting && segment.active)
              segment.prevActive = true
          }

          // Copy forward status for skip aheads
          if(!segment.hasDot) {
            segment.completed = prevSegment.completed
          }
        }
      })
    }
  }
  
}
</script>